import cx from 'classnames'
import { useState, useRef } from 'react'

import Styles from './PurchaseContentCreditsModal.module.scss'

import { Modal, useModals } from 'almanac-ui/Modal'
import { StripePrice } from 'components/Settings/Workspace/VirtualMarketerUpgradeCta/types'
import { Button } from 'blaze-ui/Button'
import { Text } from 'blaze-ui/Text/Text'
import { formatPrice } from 'components/Settings/TieredBilling/utils'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { Add, Minus } from 'almanac-ui/icons'
import { StripePaymentForm, StripePaymentFormRef } from 'components/StripePaymentForm'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useSetupIntent } from 'components/TimeTriggerPaywall/screens/Checkout/Checkout'
import { WorkspaceClient } from 'api/clients/WorkspaceClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { AddMoreContentModal } from 'screens/Calendar/WeekViewV2/BatchContentActions/Modals/AddMoreContentModal/AddMoreContentModal'
import { Heading } from 'blaze-ui/Heading/Heading'
import { useRootStore } from 'stores/RootStore'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useUpdateAutoReloadCredits } from 'hooks/useUpdateAutoReloadCredits'
import { EnableAutoReload } from 'components/Settings/TieredBilling/components/AutopilotDetails/components/PurchaseContentCreditsModal/components/EnableAutoReload/EnableAutoReload'

export const PurchaseContentCreditsModal = ({
  price,
  defaultBatchCount = 1,
  onSuccess,
  closeOnSuccess = true,
}: {
  price: StripePrice
  onSuccess?: () => void
  defaultBatchCount?: number
  closeOnSuccess?: boolean
}) => {
  const [shouldAutoReloadCredits, setAutoReloadCredits] = useState(true)
  const [batches, setBatches] = useState(defaultBatchCount)
  const [isProcessing, setIsProcessing] = useState(false)
  const { closeModal, openModal } = useModals()
  const currentWorkspaceId = useCurrentWorkspaceId()
  const { data: clientSecret, isLoading } = useSetupIntent(currentWorkspaceId)
  const stripeFormRef = useRef<StripePaymentFormRef>(null)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const { creditUsageStore } = useRootStore()
  const isAutoReloadCreditsEnabled = useFlipperFlag('auto_reload_credits')
  const updateAutoReloadCredits = useUpdateAutoReloadCredits()

  const handlePaymentMethodCreated = async (paymentMethodId?: string) => {
    if (!currentWorkspaceId) return

    setIsProcessing(true)

    const res = await WorkspaceClient.purchaseContentCredits(currentWorkspaceId, batches, price.id, paymentMethodId)

    if (res.type === SUCCESS_RESPONSE_TYPE) {
      if (isAutoReloadCreditsEnabled) {
        const autoReloadLimit = shouldAutoReloadCredits ? 500 : null
        updateAutoReloadCredits.mutate({ autoReloadCredits: shouldAutoReloadCredits, autoReloadLimit })
      }
      showToastMessage('Success! Add more content now.', 'success', {
        type: 'button',
        variant: 'proceed',
        label: 'Create Content',
        onPress: () => {
          openModal(AddMoreContentModal, { allowWizardsModal: true })
        },
      })
      creditUsageStore.refetch(currentWorkspaceId)
      onSuccess?.()
      if (closeOnSuccess) {
        closeModal()
      }
      setIsProcessing(false)
      return
    }

    setIsProcessing(false)
    showToastMessage('Failed to purchase credits', 'error')
  }

  const handleError = (error: string) => {
    showToastMessage(error, 'error')
  }

  const addCredits = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (showConfirmation) {
      if (stripeFormRef.current) {
        await stripeFormRef.current.submitPayment()
      }
      return
    }

    setShowConfirmation(true)
  }

  const toggleAutoReloadCredits = () => {
    setAutoReloadCredits(!shouldAutoReloadCredits)
  }

  return (
    <Modal.Root onPressOutside={closeModal}>
      <Modal.Header title={showConfirmation ? 'Confirm purchase' : 'Purchase more content credits to continue'} />
      <form onSubmit={addCredits}>
        <Modal.Content>
          <div className={cx(Styles.content, { [Styles.showConfirmation]: showConfirmation })}>
            {showConfirmation ? (
              <div className={Styles.confirmationContainer}>
                <Text>You'll be immediately charged ${formatPrice((price.unit_amount * batches) / 100)}.</Text>
                <div className={Styles.confirmationTotal}>
                  <Text variant="largeList">Total</Text>
                  <Heading level={4} color="var(--dark-80)">
                    ${formatPrice((price.unit_amount * batches) / 100)}
                  </Heading>
                </div>
              </div>
            ) : (
              <div className={Styles.controlsAndNote}>
                <div className={Styles.creditsSection}>
                  <div className={Styles.creditsInput}>
                    <Text variant="largeList">Add-on credits</Text>
                    <div className={Styles.batchControls}>
                      <IconButton
                        aria-label="Decrease batch"
                        variant="ghost-tertiary"
                        onPress={() => setBatches(Math.max(1, batches - 1))}
                        icon={Minus}
                      />
                      <div className={Styles.batchInput}>
                        <Heading level={5} color="var(--dark-80)">
                          {batches * 100}
                        </Heading>
                      </div>
                      <IconButton
                        aria-label="Increase batch"
                        variant="ghost-tertiary"
                        onPress={() => setBatches(batches + 1)}
                        icon={Add}
                      />
                    </div>
                  </div>
                  <Text>${formatPrice((price.unit_amount * batches) / 100)}</Text>
                </div>
                <Text variant="secondary" color="var(--dark-40)">
                  Note: Credits are purchased in 100 increments.
                </Text>
              </div>
            )}

            {isAutoReloadCreditsEnabled && (
              <div className={cx({ [Styles.hidden]: showConfirmation })}>
                <EnableAutoReload
                  shouldAutoReloadCredits={shouldAutoReloadCredits}
                  toggleAutoReloadCredits={toggleAutoReloadCredits}
                  name="enable-auto-reload-credits"
                />
              </div>
            )}

            <div className={cx(Styles.paymentSection, { [Styles.hidden]: showConfirmation })}>
              <StripePaymentForm
                ref={stripeFormRef}
                clientSecret={clientSecret}
                onPaymentMethodCreated={handlePaymentMethodCreated}
                onError={handleError}
                isProcessing={isProcessing}
                setIsProcessing={setIsProcessing}
              />
            </div>
          </div>
        </Modal.Content>
        <Modal.Footer>
          <Modal.FooterContent slot="left">
            <Button variant="tertiary" onPress={() => (showConfirmation ? setShowConfirmation(false) : closeModal())}>
              {showConfirmation ? 'Back' : 'Cancel'}
            </Button>
          </Modal.FooterContent>
          <Modal.FooterButton type="submit" isDisabled={batches === 0 || isProcessing || isLoading || !clientSecret}>
            {isProcessing && 'Processing...'}
            {!isProcessing && showConfirmation && 'Pay Now'}
            {!isProcessing && !showConfirmation && 'Review Amount'}
          </Modal.FooterButton>
        </Modal.Footer>
      </form>
    </Modal.Root>
  )
}
